import React, { useState, useEffect } from 'react';
import BrandMark from './BrandMark';
import { logout, getAdmin } from '../auth';
import { dashboardAPI } from '../api';
import ServiceStatus from './ServiceStatus';
import CacheStatus from './CacheStatus';
import MetricsChart from './MetricsChart';
import './Dashboard.css';

const Dashboard = () => {
  const [admin, setAdmin] = useState(null);
  const [servicesHealth, setServicesHealth] = useState(null);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [queuesInfo, setQueuesInfo] = useState(null);
  const [systemMetrics, setSystemMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    const adminData = getAdmin();
    setAdmin(adminData);
    loadData();
    
    // Автообновление каждые 10 секунд
    const interval = setInterval(loadData, 10000);
    
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setError('');
      
      const [health, cache, metricsData, stats, queues, system] = await Promise.all([
        dashboardAPI.getServicesHealth().catch(() => null),
        dashboardAPI.getCacheInfo().catch(() => null),
        dashboardAPI.getMetrics().catch(() => null),
        dashboardAPI.getCacheStats().catch(() => null),
        dashboardAPI.getQueuesInfo().catch(() => null),
        dashboardAPI.getSystemMetrics().catch(() => null),
      ]);
      
      if (health) setServicesHealth(health.data);
      if (cache) setCacheInfo(cache.data);
      if (metricsData) setMetrics(metricsData.data);
      if (stats) setCacheStats(stats.data);
      if (queues) setQueuesInfo(queues.data);
      if (system) setSystemMetrics(system.data);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const getQueueStatus = (value) => {
    if (value === 0) return 'success';
    if (value < 5) return 'warning';
    return 'danger';
  };

  return (
    <div className="dashboard">
      {/* Premium Header */}
      <header className="dashboard-header">
        <div className="header-left">
          <div className="logo">
            <BrandMark size={28} className="logo-icon" />
            <span className="logo-text">Balancify</span>
            <span className="logo-badge">Admin</span>
          </div>
        </div>
        <div className="header-right">
          {lastUpdate && (
            <span className="last-update">
              Обновлено: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
          {admin && (
            <div
              className="admin-badge"
              onClick={() => { window.location.href = '/profile'; }}
              style={{ cursor: 'pointer' }}
              title="Профиль: пароль и системный Telegram"
            >
              <span className="admin-avatar">{admin.username?.charAt(0).toUpperCase()}</span>
              <div className="admin-info">
                <span className="admin-name">{admin.username}</span>
                <span className="admin-role">{admin.role}</span>
              </div>
            </div>
          )}
          <button
            onClick={() => { window.location.href = '/tasks'; }}
            className="btn-logout"
            title="Задачи Celery: очередь, зависшие задачи, отмена"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6"/>
              <line x1="3" y1="12" x2="3.01" y2="12"/>
              <line x1="3" y1="18" x2="3.01" y2="18"/>
            </svg>
            Задачи
          </button>
          <button
            onClick={() => { window.location.href = '/profile'; }}
            className="btn-logout"
            title="Профиль: пароль и системный Telegram"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Профиль
          </button>
          <button onClick={logout} className="btn-logout">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Выйти
          </button>
        </div>
      </header>

      <main className="dashboard-content">
        {error && <div className="error fade-in">{error}</div>}
        
        {loading && !servicesHealth && (
          <div className="loading-screen">
            <div className="loading-spinner"></div>
            <p>Загрузка данных...</p>
          </div>
        )}

        {/* Services Status Grid */}
        {servicesHealth && (
          <section className="section fade-in" style={{animationDelay: '0.1s'}}>
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
              </svg>
              Статус системы
            </h2>
            <ServiceStatus services={servicesHealth.services} />
          </section>
        )}

        {/* Business Metrics */}
        {systemMetrics && (
          <section className="section fade-in" style={{animationDelay: '0.2s'}}>
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              Бизнес-метрики
            </h2>
            <div className="metrics-grid">
              <div className="metric-card gradient-blue">
                <div className="metric-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <span className="metric-value">{systemMetrics.users?.total || 0}</span>
                  <span className="metric-label">Пользователей</span>
                </div>
              </div>
              
              <div className="metric-card gradient-purple">
                <div className="metric-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <span className="metric-value">{systemMetrics.strategies?.total || 0}</span>
                  <span className="metric-label">Всего стратегий</span>
                </div>
              </div>
              
              <div className="metric-card gradient-green">
                <div className="metric-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <span className="metric-value">{systemMetrics.strategies?.active || 0}</span>
                  <span className="metric-label">Активных</span>
                </div>
              </div>
              
              <div className="metric-card gradient-orange">
                <div className="metric-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <span className="metric-value">{systemMetrics.admins?.active || 0}</span>
                  <span className="metric-label">Администраторов</span>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* DataCollector Performance */}
        {metrics && (
          <section className="section fade-in" style={{animationDelay: '0.3s'}}>
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <line x1="3" y1="9" x2="21" y2="9"/>
                <line x1="9" y1="21" x2="9" y2="9"/>
              </svg>
              DataCollector Performance
            </h2>
            <MetricsChart metrics={metrics} />
          </section>
        )}

        {/* Two Column Layout */}
        <div className="two-column-grid fade-in" style={{animationDelay: '0.4s'}}>
          {/* Redis Cache */}
          {cacheInfo && (
            <section className="section">
              <h2 className="section-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                </svg>
                Redis Cache
              </h2>
              <CacheStatus cacheInfo={cacheInfo} />
            </section>
          )}

          {/* Queues & Cache Stats */}
          <div className="side-panels">
            {/* Queues */}
            {queuesInfo && (
              <div className="panel">
                <h3 className="panel-title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="8" y1="6" x2="21" y2="6"/>
                    <line x1="8" y1="12" x2="21" y2="12"/>
                    <line x1="8" y1="18" x2="21" y2="18"/>
                    <line x1="3" y1="6" x2="3.01" y2="6"/>
                    <line x1="3" y1="12" x2="3.01" y2="12"/>
                    <line x1="3" y1="18" x2="3.01" y2="18"/>
                  </svg>
                  Очереди Redis
                </h3>
                <div className="queue-list">
                  <div className={`queue-item queue-${getQueueStatus(queuesInfo.queues?.strategy_registrations || 0)}`}>
                    <span className="queue-label">Регистрации стратегий</span>
                    <span className="queue-value">{queuesInfo.queues?.strategy_registrations || 0}</span>
                  </div>
                  <div className={`queue-item queue-${getQueueStatus(queuesInfo.queues?.pair_registrations || 0)}`}>
                    <span className="queue-label">Регистрации пар</span>
                    <span className="queue-value">{queuesInfo.queues?.pair_registrations || 0}</span>
                  </div>
                  <div className={`queue-item queue-${getQueueStatus(queuesInfo.queues?.strategy_unregistrations || 0)}`}>
                    <span className="queue-label">Отмены регистраций</span>
                    <span className="queue-value">{queuesInfo.queues?.strategy_unregistrations || 0}</span>
                  </div>
                </div>
                <p className="queue-hint">Значения должны быть 0 при нормальной работе</p>
              </div>
            )}

            {/* Cache Stats */}
            {cacheStats && (
              <div className="panel">
                <h3 className="panel-title">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="20" x2="18" y2="10"/>
                    <line x1="12" y1="20" x2="12" y2="4"/>
                    <line x1="6" y1="20" x2="6" y2="14"/>
                  </svg>
                  Статистика кэша
                </h3>
                <div className="cache-stats-grid">
                  <div className="cache-stat">
                    <span className="cache-stat-value success">{formatNumber(cacheStats.cache_hits)}</span>
                    <span className="cache-stat-label">Cache Hits</span>
                  </div>
                  <div className="cache-stat">
                    <span className="cache-stat-value warning">{formatNumber(cacheStats.cache_misses)}</span>
                    <span className="cache-stat-label">Cache Misses</span>
                  </div>
                </div>
                <div className="hit-rate-bar">
                  <div className="hit-rate-label">
                    <span>Hit Rate</span>
                    <span className="hit-rate-value">{cacheStats.hit_rate_percent || 0}%</span>
                  </div>
                  <div className="hit-rate-track">
                    <div 
                      className="hit-rate-fill" 
                      style={{width: `${cacheStats.hit_rate_percent || 0}%`}}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="dashboard-footer">
        <p>Balancify Admin Panel • v1.0.0 • {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
};

export default Dashboard;
